import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = "https://newalemny.3almny.app/api";

// جلب جميع التقارير للطالب
export const fetchStudentReports = createAsyncThunk(
  'studentReports/fetchStudentReports',
  async ({ studentId, classId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      
      if (!token) {
        throw new Error('غير مصرح لك بالوصول');
      }

      let url;
      if (classId === 0) {
        // لولي الأمر: استخدام endpoint مختلف
        url = `${BASE_URL}/parent/my_child_sub_repo/0?child_id=${studentId}&per_page=900`;
      } else {
        // للمعلم: استخدام endpoint عادي
        url = `${BASE_URL}/student_reports/get_all/${studentId}?class_id=${classId}`;
      }

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.statusCode === 200) {
        // التقارير تأتي في result.data
        return response.data.result.data || [];
      } else {
        throw new Error(response.data.message || 'فشل في جلب التقارير');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'حدث خطأ في جلب التقارير');
    }
  }
);

// جلب تفاصيل تقرير معين
export const fetchReportDetails = createAsyncThunk(
  'studentReports/fetchReportDetails',
  async (reportId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      
      if (!token) {
        throw new Error('غير مصرح لك بالوصول');
      }

      const response = await axios.get(`${BASE_URL}/student_reports/report_details/${reportId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.statusCode === 200) {
        return response.data.result;
      } else {
        throw new Error(response.data.message || 'فشل في جلب تفاصيل التقرير');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'حدث خطأ في جلب تفاصيل التقرير');
    }
  }
);

// إنشاء تقرير جديد
export const createStudentReport = createAsyncThunk(
  'studentReports/createStudentReport',
  async (reportData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      
      if (!token) {
        throw new Error('غير مصرح لك بالوصول');
      }

      const response = await axios.post(`${BASE_URL}/student_reports/make_report`, reportData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.statusCode === 200) {
        return response.data.result;
      } else {
        throw new Error(response.data.message || 'فشل في إنشاء التقرير');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'حدث خطأ في إنشاء التقرير');
    }
  }
);

// Get child reports for parent
export const getChildReports = createAsyncThunk(
  "studentReports/getChildReports",
  async ({ childId, subjectId, page = 1, perPage = 900 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب");
      }

      const response = await fetch(`${BASE_URL}/parent/my_child_sub_repo/${subjectId}?child_id=${childId}&per_page=${perPage}`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب تقارير الابن");
      }

      return data.result;
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

const initialState = {
  reports: [],
  currentReport: null,
  loading: false,
  creatingReport: false,
  error: null,
  successMessage: null,
  childReports: null,
  childReportsLoading: false,
};

const studentReportsSlice = createSlice({
  name: 'studentReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearCurrentReport: (state) => {
      state.currentReport = null;
    },
    resetState: (state) => {
      state.reports = [];
      state.currentReport = null;
      state.loading = false;
      state.creatingReport = false;
      state.error = null;
      state.successMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // جلب التقارير
      .addCase(fetchStudentReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload;
        state.error = null;
      })
      .addCase(fetchStudentReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // جلب تفاصيل التقرير
      .addCase(fetchReportDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
        state.error = null;
      })
      .addCase(fetchReportDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // إنشاء تقرير
      .addCase(createStudentReport.pending, (state) => {
        state.creatingReport = true;
        state.error = null;
      })
      .addCase(createStudentReport.fulfilled, (state, action) => {
        state.creatingReport = false;
        state.successMessage = 'تم إنشاء التقرير بنجاح';
        state.reports.unshift(action.payload);
        state.error = null;
      })
      .addCase(createStudentReport.rejected, (state, action) => {
        state.creatingReport = false;
        state.error = action.payload;
      })
      // جلب تقارير الأبناء
      .addCase(getChildReports.pending, (state) => {
        state.childReportsLoading = true;
        state.error = null;
      })
      .addCase(getChildReports.fulfilled, (state, action) => {
        state.childReportsLoading = false;
        state.childReports = action.payload;
        state.error = null;
      })
      .addCase(getChildReports.rejected, (state, action) => {
        state.childReportsLoading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  clearSuccessMessage, 
  clearCurrentReport, 
  resetState 
} = studentReportsSlice.actions;

export default studentReportsSlice.reducer;
